import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store'
import { registerThunk, clearError } from '../../store/slices/authSlice'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, Mail, Phone, User } from 'lucide-react'

const schema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  email:       z.string().email('Invalid email address'),
  password:    z.string().min(6, 'Password must be at least 6 characters'),
  phone:       z.string().regex(/^\d{10}$/, 'Phone must be 10 digits').optional().or(z.literal('')),
})
type FormData = z.infer<typeof schema>

export default function RegisterPage() {
  const dispatch  = useAppDispatch()
  const navigate  = useNavigate()
  const { loading, error } = useAppSelector((s) => s.auth)
  const [showPwd, setShowPwd] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => { dispatch(clearError()) }, [dispatch])

  const onSubmit = async (data: FormData) => {
    const result = await dispatch(registerThunk({ ...data, phone: data.phone || undefined }))
    if (registerThunk.fulfilled.match(result)) {
      toast.success('Account created! Please verify your email.')
      navigate('/verify-otp', { state: { email: data.email } })
    } else {
      toast.error((result.payload as string) || 'Registration failed. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FDFAFF' }}>

      {/* ── Left: Branding (desktop only) ── */}
      <div className="hidden lg:flex flex-col justify-center px-16 w-[52%] relative overflow-hidden" style={{ background: 'linear-gradient(145deg, #6D28D9 0%, #8B5CF6 50%, #A78BFA 100%)' }}>
        <div className="absolute top-[-80px] right-[-60px] w-72 h-72 rounded-full opacity-20" style={{ background: 'white' }} />
        <div className="absolute bottom-[-60px] left-[-40px] w-56 h-56 rounded-full opacity-10" style={{ background: 'white' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <span className="text-white font-bold text-2xl leading-none select-none">R</span>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">Referrals</span>
          </div>
          <h1 className="text-4xl font-bold text-white leading-snug max-w-sm mb-8">
            Join thousands getting hired through referrals.
          </h1>
          <ul className="space-y-4">
            {[
              'Get referred directly by insiders at top companies',
              'Employers earn rewards for every successful hire',
              'Secure escrow payments — released only on success',
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: 'rgba(255,255,255,0.25)' }}>✓</span>
                <span className="text-sm text-white/85">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right: Form ── */}
      <div className="flex-1 flex items-center justify-center py-10 px-6 lg:px-20">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-9 h-9 bg-primary-600 rounded flex items-center justify-center">
              <span className="text-white font-bold text-lg leading-none">R</span>
            </div>
            <span className="text-2xl font-bold text-primary-600">Referrals</span>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #EDE9FE', boxShadow: '0 8px 40px rgba(139,92,246,0.16)' }}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">Create account</h2>
            <p className="text-sm text-gray-500 mb-6">Join the Referrals professional network</p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                <div className="relative">
                  <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    {...register('displayName')}
                    className="input pl-9"
                    placeholder="John Doe"
                    autoComplete="name"
                  />
                </div>
                {errors.displayName && (
                  <p className="text-red-600 text-xs mt-1">{errors.displayName.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    {...register('email')}
                    type="email"
                    autoComplete="email"
                    className="input pl-9"
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    {...register('password')}
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input pl-9 pr-10"
                    placeholder="Min. 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-xs mt-1">{errors.password.message}</p>
                )}
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Phone <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    {...register('phone')}
                    type="tel"
                    autoComplete="tel"
                    className="input pl-9"
                    placeholder="9876543210"
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-600 text-xs mt-1">{errors.phone.message}</p>
                )}
              </div>

              {/* Error banner */}
              {error && (
                <div className="bg-red-50 text-red-700 text-sm px-4 py-2.5 rounded-md border border-red-200">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center py-3 px-4 bg-primary-600 text-white text-base font-semibold rounded-full hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? 'Creating account...' : 'Agree & Join'}
              </button>
            </form>

            {/* Terms note */}
            <p className="text-xs text-gray-400 text-center mt-3 leading-relaxed">
              By clicking Agree & Join, you agree to our{' '}
              <span className="underline cursor-pointer text-gray-500">Terms</span> and{' '}
              <span className="underline cursor-pointer text-gray-500">Privacy Policy</span>.
            </p>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-500">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Login link */}
            <p className="text-center text-sm text-gray-600">
              Already on Referrals?{' '}
              <Link to="/login" className="font-semibold text-primary-600 no-underline hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
